import { useMutation } from '@tanstack/react-query'
import { App, Button, Flex, Form, Tooltip, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import {
  authorizationV2PasswordRequestCreateMutation,
  type AuthorizationV2PasswordRequestCreateData
} from 'src/api/main'
import { useResendCountdown } from 'src/features/auth/hooks'
import { paths } from 'src/router'
import { useInlineApiErrors } from 'src/utils'

import { AuthHeader, AuthLink, EmailInput } from '../components'

const { Text } = Typography

type ResetPasswordFormValues = Pick<
  AuthorizationV2PasswordRequestCreateData['body'],
  'username'
>

const redirectUrl = `${window.location.origin}${paths.auth.resetPasswordConfirm}`
// The reset endpoint returns no server cooldown, so throttle resends on the
// client — same idea as the e-mail OTP resend (disabled until the wait elapses).
const RESEND_COOLDOWN_MS = 60_000

type ResetPasswordLocationState = {
  username?: string
}

const getUsernameState = (username?: string) => {
  const nextUsername = username?.trim()

  return nextUsername ? { username: nextUsername } : undefined
}

export const ResetPassword = () => {
  const [form] = Form.useForm<ResetPasswordFormValues>()
  const location = useLocation()
  const [emailSent, setEmailSent] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [cooldownStartedAt, setCooldownStartedAt] = useState<string | null>(
    null
  )
  const { message } = App.useApp()
  const { showApiErrors, errorAlert } = useInlineApiErrors()

  const { t } = useTranslation()
  const locationState = useMemo(
    () => location.state as ResetPasswordLocationState | null,
    [location.state]
  )
  const initialUsername =
    typeof locationState?.username === 'string' ? locationState.username : ''
  const watchedUsername = Form.useWatch('username', form)
  const passwordLoginState = getUsernameState(
    typeof watchedUsername === 'string' ? watchedUsername : initialUsername
  )
  const submittedPasswordLoginState = getUsernameState(submittedEmail)
  const resendCooldown = useResendCountdown({
    createdAt: cooldownStartedAt,
    waitSeconds: RESEND_COOLDOWN_MS / 1000
  })

  useEffect(() => {
    if (!initialUsername || form.getFieldValue('username')) {
      return
    }

    form.setFieldValue('username', initialUsername)
  }, [form, initialUsername])

  const { mutate: sendResetEmail, isPending } = useMutation({
    ...authorizationV2PasswordRequestCreateMutation(),
    onSuccess: () => {
      message.success(t('email_sent_success'))
      setEmailSent(true)
      setCooldownStartedAt(new Date().toISOString())
    },
    onError: (e) => showApiErrors(e, form)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    setSubmittedEmail(values.username)
    sendResetEmail({
      body: {
        username: values.username,
        redirect_url: redirectUrl
      }
    })
  }

  const handleResend = () => {
    if (!resendCooldown.canResend || !submittedEmail) {
      return
    }
    sendResetEmail({
      body: {
        username: submittedEmail,
        redirect_url: redirectUrl
      }
    })
  }

  const tryAgain = () => {
    setEmailSent(false)
    setSubmittedEmail('')
    setCooldownStartedAt(null)
    form.resetFields()
  }

  if (emailSent) {
    return (
      <Flex orientation="vertical" gap={16} className="w-full">
        <AuthHeader
          title={t('forgot_password')}
          subtitle={t('check_email_instruction')}
        />

        <Flex orientation="vertical" gap={16}>
          <Flex>
            <Tooltip
              title={
                resendCooldown.canResend
                  ? undefined
                  : t('auth.resendInSeconds', {
                      count: resendCooldown.resendInSeconds
                    })
              }
            >
              <span style={{ display: 'inline-flex' }}>
                <Button
                  type="link"
                  disabled={!resendCooldown.canResend}
                  loading={isPending}
                  onClick={handleResend}
                  style={{ paddingInline: 0 }}
                >
                  {t('auth.emailOtp.linkResend')}
                </Button>
              </span>
            </Tooltip>
          </Flex>

          <AuthLink to={paths.auth.resetPassword} onClick={tryAgain}>
            {t('reset_password_again')}
          </AuthLink>

          <Flex align="center" justify="center" gap={6} wrap="wrap">
            <Text type="secondary">{t('have_account')}</Text>
            <AuthLink
              to={paths.auth.loginPassword}
              state={submittedPasswordLoginState}
            >
              {t('sign_in')}
            </AuthLink>
          </Flex>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex orientation="vertical" gap={16} className="w-full">
      <AuthHeader
        title={t('forgot_password')}
        subtitle={t('enter_email_send_new_one')}
      />

      {errorAlert}

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        requiredMark={false}
        noValidate
        id="reset-password-form"
      >
        <Flex orientation="vertical" gap={16}>
          <EmailInput name="username" autoFocus label={t('email')} />

          <Button
            block
            size="large"
            onClick={onSubmit}
            type="primary"
            loading={isPending}
            form="reset-password-form"
            htmlType="submit"
          >
            {t('submit')}
          </Button>
        </Flex>
      </Form>

      <Flex align="center" justify="center" gap={6} wrap="wrap">
        <Text type="secondary">{t('have_account')}</Text>
        <AuthLink to={paths.auth.loginPassword} state={passwordLoginState}>
          {t('sign_in')}
        </AuthLink>
      </Flex>
    </Flex>
  )
}
